import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'
import { useAppStore } from '@core/store'
import ModuleChrome from '@core/layout/ModuleChrome'
import { useJanelaNaFrente } from '@core/layout/camadas'
import {
  clampFloatRect,
  type FloatRect,
  type WorkspaceLayoutMode,
} from '@core/store/workspaceLayoutSlice'

type Drag =
  | { kind: 'move'; dx: number; dy: number }
  | { kind: 'resize'; x0: number; y0: number; w0: number; h0: number }

/**
 * Painel flutuante do módulo: fica por cima da página, arrasta-se pela
 * barra de título e redimensiona-se pelo canto inferior direito. O
 * viewer 3D fica inteiro por baixo e continua a receber rato fora do
 * painel.
 *
 * A geometria vive em estado local durante o arrasto e só vai ao store
 * (e a localStorage) quando o ponteiro larga: escrever a cada
 * `pointermove` dava uma escrita a localStorage por frame.
 */
function ModuleFloatingPanel({
  title,
  mode,
  setMode,
  onClose,
  children,
}: {
  title: string
  mode: WorkspaceLayoutMode
  setMode: (m: WorkspaceLayoutMode) => void
  onClose: () => void
  children: ReactNode
}) {
  const storedRect = useAppStore((s) => s.workspaceFloatRect)
  const commitRect = useAppStore((s) => s.setWorkspaceFloatRect)
  const [rect, setRect] = useState<FloatRect>(storedRect)
  const [drag, setDrag] = useState<Drag | null>(null)
  const { z, trazer } = useJanelaNaFrente()
  // A geometria em curso vai para uma ref para os handlers de arrasto a
  // lerem sem se redeclararem a cada pixel. Escrita em efeito e não no
  // render (mexer numa ref durante o render não é seguro), e declarado
  // ANTES dos efeitos que a lêem, para correr primeiro.
  const rectRef = useRef(rect)
  useEffect(() => {
    rectRef.current = rect
  })

  // O painel guardado pode vir de um ecrã maior. Reenquadrar quando a
  // janela muda de tamanho evita o painel fora de vista.
  useEffect(() => {
    const onResize = () => setRect((r) => clampFloatRect(r))
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  useEffect(() => {
    if (!drag) return
    const onMove = (e: PointerEvent) => {
      if (drag.kind === 'move') {
        setRect((r) =>
          clampFloatRect({ ...r, x: e.clientX - drag.dx, y: e.clientY - drag.dy }),
        )
      } else {
        setRect((r) =>
          clampFloatRect({
            ...r,
            w: drag.w0 + (e.clientX - drag.x0),
            h: drag.h0 + (e.clientY - drag.y0),
          }),
        )
      }
      e.preventDefault()
    }
    const onUp = () => {
      setDrag(null)
      commitRect(rectRef.current)
    }
    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', onUp)
    return () => {
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
    }
  }, [drag, commitRect])

  const onDragStart = useCallback((e: React.PointerEvent) => {
    if (e.button !== 0) return
    const r = rectRef.current
    setDrag({ kind: 'move', dx: e.clientX - r.x, dy: e.clientY - r.y })
    e.preventDefault()
  }, [])

  const onResizeStart = useCallback((e: React.PointerEvent) => {
    if (e.button !== 0) return
    const r = rectRef.current
    setDrag({
      kind: 'resize',
      x0: e.clientX,
      y0: e.clientY,
      w0: r.w,
      h0: r.h,
    })
    e.preventDefault()
  }, [])

  return (
    <div
      role="dialog"
      aria-label={`Painel flutuante: ${title}`}
      data-testid="module-floating-panel"
      onPointerDownCapture={trazer}
      style={{ zIndex: z, left: rect.x, top: rect.y, width: rect.w, height: rect.h }}
      className="fixed flex flex-col overflow-hidden rounded-md border border-[var(--color-border)] bg-[var(--color-bg)] shadow-2xl"
    >
      <ModuleChrome
        title={title}
        mode={mode}
        setMode={setMode}
        onClose={onClose}
        onDragStart={onDragStart}
      />
      <div className="min-h-0 min-w-0 flex-1 overflow-auto">{children}</div>
      <div
        role="separator"
        aria-label="Redimensionar o painel"
        onPointerDown={onResizeStart}
        title="Arrastar para redimensionar"
        className="absolute bottom-0 right-0 h-4 w-4 cursor-nwse-resize"
        style={{
          background:
            'linear-gradient(135deg, transparent 50%, var(--color-border) 50%)',
        }}
      />
    </div>
  )
}

export default ModuleFloatingPanel
